import { FC } from 'react';
import { SleepBar } from '../../../KanbanView/CoreActions/CoreActionModal/ActionModalViews/DailyAnalysisModalView/SleepAnalysis/SleepBar';
import { Typography } from '@mui/material';
import { SleepStage, MetricHRSummary } from '@demind-inc/core';
import clsx from 'clsx';
import './SleepBarAndInfo.scss';
import { sleepSummaryColor, sleepSummaryText } from '../../constants';
import { translateSecondsToHourAndMinutes } from '../../../../helpers';
import { useTranslation } from 'react-i18next';
import { formatMinutes } from '../../i18n';
interface SleepBarAndInfoProps {
  targetSleepStage: SleepStage;
  targetAwakeTime: string;
  targetSleepTime: string;
  hasNoSleepStage?: boolean;
  className?: string;
  heartRateSummary?: MetricHRSummary;
}

const heartRateSummaryNameMap: Record<keyof MetricHRSummary, { label: string; unit: string }> = {
  restingHRBpm: { label: 'Resting HR', unit: 'bpm' },
  avgHRBpm: { label: 'Avg HR', unit: 'bpm' },
  minHRBpm: { label: 'Min HR', unit: 'bpm' },
  maxHRBpm: { label: 'Max HR', unit: 'bpm' },
  avgHRVRmssd: { label: 'Avg HRV (RMSSD)', unit: 'ms' },
  avgHRVSdnn: { label: 'Avg HRV (SDNN)', unit: 'ms' },
};

const SleepBarAndInfo: FC<SleepBarAndInfoProps> = ({
  targetSleepStage,
  targetAwakeTime,
  targetSleepTime,
  hasNoSleepStage = false,
  className,
  heartRateSummary,
}) => {
  const { t } = useTranslation();
  const heartRateSummaryEntries = heartRateSummary
    ? (Object.entries(heartRateSummary) as [keyof MetricHRSummary, number][]).filter(
        ([, value]) => value !== 0 && value !== null && value !== undefined,
      )
    : [];

  return (
    <div className={clsx('sleep-bar-info-container', className)}>
      <SleepBar
        sleepStage={targetSleepStage}
        awakeTime={targetAwakeTime}
        sleepTime={targetSleepTime}
      />
      {heartRateSummaryEntries.length > 0 && (
        <div className="sleep-bar-info-container__metrics">
          {heartRateSummaryEntries.map(([key, value]) => {
            const { label, unit } = heartRateSummaryNameMap[key];

            return (
              <div className="sleep-bar-info-container__metrics__item">
                <Typography
                  variant="subtitle2"
                  className="sleep-bar-info-container__metrics__item__label"
                >
                  {t(`data.sleep.heartRate.${key}`, { defaultValue: label })}
                </Typography>
                <Typography
                  variant="subtitle2"
                  className="sleep-bar-info-container__metrics__item__value"
                >
                  {Math.round(value)} {unit}
                </Typography>
              </div>
            );
          })}
        </div>
      )}
      {!hasNoSleepStage && (
        <div className="sleep-bar-info-container__stage-info">
          {Object.keys(targetSleepStage ?? {}).map((key, value) => {
            const { hours, minutes } = translateSecondsToHourAndMinutes(targetSleepStage[key]);
            return (
              <div key={value} className="sleep-bar-info-container__stage-info__item">
                <div className="sleep-bar-info-container__sleep-stage-bullet-container">
                  <div
                    style={{
                      background: sleepSummaryColor[key],
                    }}
                    className="sleep-bar-info-container__sleep-stage-bullet-container__bullet"
                  />
                  <Typography variant="subtitle2">
                    {t(`data.sleep.stages.${key}`, { defaultValue: sleepSummaryText[key] })}
                  </Typography>
                </div>
                <div className="sleep-bar-info-container__stage-info__time">
                  <Typography variant="subtitle2">
                    {t('data.common.hoursValue', { hours })}
                  </Typography>
                  <Typography variant="subtitle2">{formatMinutes(t, minutes)}</Typography>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SleepBarAndInfo;
